/*
Purpose: Admin/advanced dashboard to review and iterate on AI prompts and responses.
Audience: Internal users and developers.
What it does:
- Surfaces prompts, parameters, and responses for inspection
- Helps tune models and templates used by analysis flows
Interplay:
- Complements aiModelsConfig and promptAnalysis helpers
*/
/**
 * ==================================================================================
 * AI PROMPT REVIEW DASHBOARD - UI COMPONENT
 * ==================================================================================
 * 
 * PURPOSE:
 * This React component provides a comprehensive user interface for reviewing,
 * analyzing, and auditing AI model prompts and parameters used in forensic
 * evidence analysis. It's designed for non-technical stakeholders, investigators,
 * legal teams, and administrators who need to understand and validate AI behavior.
 * 
 * FUNCTION:
 * - Renders interactive dashboard with tabs for different analysis views
 * - Displays AI model comparisons, cost analysis, and quality metrics
 * - Provides search and filtering capabilities for prompt review
 * - Generates exportable reports for compliance and legal review
 * - Shows real-time prompt analysis and forensic compliance scores
 * 
 * WHO USES THIS:
 * - Criminal investigators reviewing AI analysis capabilities
 * - Legal teams validating AI evidence for court proceedings
 * - System administrators monitoring AI model performance
 * - Compliance officers conducting forensic system audits
 * - Management reviewing cost-effectiveness of different AI models
 * 
 * KEY FEATURES:
 * - Model Comparison: Side-by-side analysis of different AI models
 * - Prompt Analysis: Detailed breakdown of forensic prompt structure
 * - Quality Metrics: Compliance scoring and bias assessment
 * - Cost Analysis: Token usage and cost-effectiveness ratings
 * - Export Functionality: Generate reports for external review
 * 
 * DEPENDENCIES:
 * - Imports analysis data from '../config/promptAnalysis.js'
 * - Uses React hooks for state management and UI interactions
 * - Relies on Font Awesome icons for UI elements
 * 
 * NOTE FOR REVIEWERS:
 * This is the UI layer only - all business logic and data analysis is handled
 * by the promptAnalysis.js file. This component focuses on presenting data
 * in an accessible, professional format suitable for legal and compliance review.
 * ==================================================================================
 */

import React, { useState, useMemo } from 'react';
import { 
  FaRobot, 
  FaEye, 
  FaDollarSign, 
  FaClock, 
  FaShieldAlt, 
  FaCheckCircle,
  FaExclamationTriangle,
  FaDownload,
  FaSearch,
  FaFileAlt
} from 'react-icons/fa';
import { generatePromptDocumentation } from '../../config/promptAnalysis';
import aiConfig from '../../config/aiModelsConfig';
import AdvancedAIControls from './AdvancedAIControls';
import '../../styles/PromptReviewDashboard.css';

const PromptDashboard = () => {
  const [activeSection, setActiveSection] = useState('executive_summary');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Prompt Preview State
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-pro');
  const [selectedTargets, setSelectedTargets] = useState(['vehicles', 'people', 'text']);
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side', 'human', 'api'
  const [showAdvancedControls, setShowAdvancedControls] = useState(false);
  
  // Advanced AI Parameters - All possible levers
  const [advancedParams, setAdvancedParams] = useState({
    // Core Model Parameters
    temperature: 0.1,
    top_p: 0.8,
    top_k: 40,
    max_output_tokens: 8192,
    frequency_penalty: 0.0,
    presence_penalty: 0.0,
    
    // Forensic-Specific Controls
    evidence_quality_threshold: 0.7,
    speculation_prevention: 'strict',
    verbosity: 'detailed',
    confidence_display: 'explicit',
    confidence_calibration: 'balanced',
    
    // Advanced Prompting Techniques
    chain_of_thought: true,
    few_shot_learning: false,
    retrieval_augmented: false,
    multi_agent_reasoning: false,
    
    // Enable/Disable flags for each parameter
    temperature_enabled: true,
    top_p_enabled: true,
    top_k_enabled: true,
    max_output_tokens_enabled: true,
    frequency_penalty_enabled: false,
    presence_penalty_enabled: false,
    evidence_quality_threshold_enabled: true,
    speculation_prevention_enabled: true,
    verbosity_enabled: true,
    confidence_display_enabled: true,
    confidence_calibration_enabled: true,
    chain_of_thought_enabled: true,
    few_shot_learning_enabled: false,
    retrieval_augmented_enabled: false,
    multi_agent_reasoning_enabled: false
  });

  // Generate documentation
  const documentation = useMemo(() => generatePromptDocumentation(), []);
  
  // Get all available models dynamically
  const availableModels = useMemo(() => aiConfig.getAvailableModels(), []);
  
  // Get current model configuration
  const currentModelConfig = useMemo(() => 
    availableModels.find(m => m.id === selectedModel), 
    [availableModels, selectedModel]
  );

  // Apply preset configurations
  const applyPreset = (presetType) => {
    let presetConfig = {};
    
    switch (presetType) {
      case 'forensic':
        // Optimized for legal proceedings - maximum accuracy and consistency
        presetConfig = {
          // Core Parameters - Very conservative for reproducible results
          temperature: 0.05,
          top_p: 0.7,
          top_k: 30,
          max_output_tokens: 4096,
          frequency_penalty: 0.1,
          presence_penalty: 0.0,
          
          // Forensic Controls - Strictest settings
          evidence_quality_threshold: 0.8,
          speculation_prevention: 'strict',
          verbosity: 'detailed',
          confidence_display: 'explicit',
          confidence_calibration: 'conservative',
          
          // Advanced Techniques - Only proven methods
          chain_of_thought: true,
          few_shot_learning: true,
          retrieval_augmented: true,
          multi_agent_reasoning: false, // Disabled for speed in court settings
          
          // Enable flags - Core forensic features only
          temperature_enabled: true,
          top_p_enabled: true,
          top_k_enabled: true,
          max_output_tokens_enabled: true,
          frequency_penalty_enabled: true,
          presence_penalty_enabled: false, // Disabled - could introduce irrelevant topics
          evidence_quality_threshold_enabled: true,
          speculation_prevention_enabled: true,
          verbosity_enabled: true,
          confidence_display_enabled: true,
          confidence_calibration_enabled: true,
          chain_of_thought_enabled: true,
          few_shot_learning_enabled: true,
          retrieval_augmented_enabled: true,
          multi_agent_reasoning_enabled: false
        };
        break;
        
      case 'exploratory':
        // Balanced for investigation - good accuracy with flexibility
        presetConfig = {
          // Core Parameters - Balanced settings
          temperature: 0.2,
          top_p: 0.8,
          top_k: 40,
          max_output_tokens: 6144,
          frequency_penalty: 0.2,
          presence_penalty: 0.1,
          
          // Forensic Controls - Moderate settings
          evidence_quality_threshold: 0.6,
          speculation_prevention: 'moderate',
          verbosity: 'detailed',
          confidence_display: 'explicit',
          confidence_calibration: 'balanced',
          
          // Advanced Techniques - Most enabled for thorough analysis
          chain_of_thought: true,
          few_shot_learning: true,
          retrieval_augmented: true,
          multi_agent_reasoning: true,
          
          // Enable flags - Most features enabled
          temperature_enabled: true,
          top_p_enabled: true,
          top_k_enabled: true,
          max_output_tokens_enabled: true,
          frequency_penalty_enabled: true,
          presence_penalty_enabled: true,
          evidence_quality_threshold_enabled: true,
          speculation_prevention_enabled: true,
          verbosity_enabled: true,
          confidence_display_enabled: true,
          confidence_calibration_enabled: true,
          chain_of_thought_enabled: true,
          few_shot_learning_enabled: true,
          retrieval_augmented_enabled: true,
          multi_agent_reasoning_enabled: true
        };
        break;
        
      case 'creative':
        // Creative analysis - may catch subtle details but less consistent
        presetConfig = {
          // Core Parameters - Higher creativity settings
          temperature: 0.4,
          top_p: 0.9,
          top_k: 60,
          max_output_tokens: 8192,
          frequency_penalty: 0.3,
          presence_penalty: 0.2,
          
          // Forensic Controls - More permissive
          evidence_quality_threshold: 0.4,
          speculation_prevention: 'relaxed',
          verbosity: 'comprehensive',
          confidence_display: 'explicit',
          confidence_calibration: 'balanced',
          
          // Advanced Techniques - All enabled for maximum insight
          chain_of_thought: true,
          few_shot_learning: false, // Disabled to allow more creative responses
          retrieval_augmented: true,
          multi_agent_reasoning: true,
          
          // Enable flags - All features enabled for maximum flexibility
          temperature_enabled: true,
          top_p_enabled: true,
          top_k_enabled: true,
          max_output_tokens_enabled: true,
          frequency_penalty_enabled: true,
          presence_penalty_enabled: true,
          evidence_quality_threshold_enabled: true,
          speculation_prevention_enabled: true,
          verbosity_enabled: true,
          confidence_display_enabled: true,
          confidence_calibration_enabled: true,
          chain_of_thought_enabled: true,
          few_shot_learning_enabled: false,
          retrieval_augmented_enabled: true,
          multi_agent_reasoning_enabled: true
        };
        break;
        
      default:
        return;
    }
    
    setAdvancedParams(prevParams => ({
      ...prevParams,
      ...presetConfig
    }));
  };

  // Filter models based on search
  const filteredModels = useMemo(() => {
    if (!searchTerm) return documentation.sections.model_comparison.models;
    
    return documentation.sections.model_comparison.models.filter(model =>
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.best_for.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, documentation]);

  const exportDocumentation = () => {
    const blob = new Blob([JSON.stringify(documentation, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-prompt-review-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Generate actual API payload based on selected model and targets
  const generateApiPayload = () => {
    // Enhanced forensic prompt using new prompting techniques
    const basePrompt = `You are a professional forensic AI analyst working on criminal investigations. 
Your analysis will be used in legal proceedings and must meet the highest standards of accuracy and objectivity.

CRITICAL REQUIREMENTS:
- Provide only factual observations without speculation
- Include confidence scores for all detections (0.0-1.0 scale)
- Use proper forensic terminology
- Maintain chain of custody awareness
- Document any limitations or uncertainties

${selectedTargets.includes('vehicles') ? `
VEHICLE ANALYSIS PROTOCOL:
1. Vehicle Identification:
   - Make, model, year (examine grille, headlights, body lines, badges)
   - Body type (sedan, SUV, truck, etc.) and size category
   - Primary and secondary colors (account for lighting conditions)
   - Confidence score for each identification (0.0-1.0)

2. License Plate Analysis:
   - Extract complete plate number with character-by-character confidence
   - Identify state/province and country
   - Assess legibility (clear/partial/obscured)
   - Note plate type (standard, specialty, temporary)

3. Distinguishing Features:
   - Document all visible damage (dents, scratches, missing parts)
   - Note modifications (aftermarket parts, tinting, lift kits)
   - Identify stickers, decals, or unique markings
   - Assess cargo or attached items

4. Condition Assessment:
   - Paint condition (new, faded, damaged, rust)
   - Tire condition and type
   - Overall maintenance level indicators
   - Age-related wear patterns

5. Movement Analysis:
   - Direction of travel and speed indicators
   - Driving behavior patterns (aggressive, cautious, erratic)
   - Parking patterns and positioning
` : ''}

${selectedTargets.includes('activities') ? `
ACTIVITY ANALYSIS PROTOCOL:
1. Primary Actions:
   - Document all visible activities (walking, running, standing, etc.)
   - Note object manipulation and tool usage
   - Assess interaction with environment

2. Social Interactions:
   - Person-to-person interactions and group dynamics
   - Communication indicators (gestures, body language)
   - Exchange of items or coordination patterns

3. Behavioral Analysis:
   - Concealment or furtive movements
   - Surveillance awareness indicators
   - Signs of planning or spontaneous behavior
   - Stress or deception indicators

4. Temporal Patterns:
   - Sequence and timing of events
   - Duration of activities
   - Entry/exit patterns and waiting periods
   - Urgency or hesitation indicators

5. Environmental Interaction:
   - Evidence tampering or destruction
   - Object placement or retrieval
   - Hiding or cleanup activities
   - Interaction with security systems
` : ''}

CONFIDENCE SCORING REQUIREMENTS:
- 0.9-1.0: Clearly visible, unambiguous identification
- 0.7-0.8: Clearly visible with minor uncertainty
- 0.5-0.6: Partially visible or obscured
- 0.3-0.4: Barely visible, high uncertainty
- 0.0-0.2: Cannot determine reliably
- MINIMUM THRESHOLD: Only report findings above ${advancedParams.evidence_quality_threshold} confidence

${advancedParams.chain_of_thought && advancedParams.chain_of_thought_enabled !== false ? `
CHAIN-OF-THOUGHT ANALYSIS REQUIRED:
Before providing final determinations, show your reasoning process:
1. INITIAL OBSERVATION: "I can see..."
2. DETAILED EXAMINATION: "Looking more closely at..."
3. CONFIDENCE ASSESSMENT: "I am confident about X because... but uncertain about Y because..."
4. FINAL DETERMINATION: "Based on this analysis..."
` : ''}

${advancedParams.few_shot_learning && advancedParams.few_shot_learning_enabled !== false ? `
FEW-SHOT LEARNING EXAMPLES:
Use these examples as a guide for analysis format and quality:

EXAMPLE 1 - Vehicle Analysis:
Input: Image of parking lot
Output: {"objects_detected": [{"category": "vehicle", "type": "sedan", "confidence": 0.92, "attributes": {"make": "Toyota", "color": "blue", "plate_number": "ABC123", "plate_confidence": 0.87}}]}

EXAMPLE 2 - Activity Analysis:
Input: Security footage
Output: {"objects_detected": [{"category": "activity", "type": "walking", "confidence": 0.95, "temporal_patterns": {"duration": "15 seconds", "urgency": "normal"}}]}

Follow this format and level of detail.
` : ''}

${advancedParams.multi_agent_reasoning && advancedParams.multi_agent_reasoning_enabled !== false ? `
MULTI-AGENT REASONING MODE:
After your analysis, perform a self-validation check:
1. Review your findings for consistency
2. Consider alternative interpretations
3. Cross-check confidence scores
4. Note any areas of uncertainty that would benefit from additional perspectives
` : ''}

${advancedParams.retrieval_augmented && advancedParams.retrieval_augmented_enabled !== false ? `
RETRIEVAL-AUGMENTED ANALYSIS:
Consider relevant forensic knowledge and best practices:
- Standard vehicle identification procedures
- Typical confidence thresholds for evidence quality
- Common sources of analysis errors
- Legal requirements for evidence documentation
` : ''}

SPECULATION PREVENTION: ${advancedParams.speculation_prevention.toUpperCase()}
${advancedParams.speculation_prevention === 'strict' ? 
  '- Never guess or infer details not directly visible\n- State "Cannot determine" rather than speculating\n- Distinguish clearly between observation and inference' :
  advancedParams.speculation_prevention === 'moderate' ?
  '- Limited inference allowed when supported by evidence\n- Clearly mark inferences as such\n- Provide reasoning for any inferences' :
  '- Educated guesses permitted when clearly labeled\n- Use contextual knowledge appropriately\n- Always indicate confidence level for inferences'
}

ANALYSIS VERBOSITY: ${advancedParams.verbosity.toUpperCase()}
CONFIDENCE DISPLAY: ${advancedParams.confidence_display} - ${
  advancedParams.confidence_display === 'explicit' ? 'Show detailed reasoning for confidence scores' :
  advancedParams.confidence_display === 'numeric' ? 'Provide numerical confidence scores only' :
  'Use qualitative confidence descriptors (High/Medium/Low)'
}

ANALYSIS TARGETS: Focus specifically on: ${selectedTargets.join(', ')}

FORENSIC OUTPUT REQUIREMENTS:
${currentModelConfig?.accuracy === 'highest' ? 
  '- Provide comprehensive analysis with detailed reasoning\n- Include confidence scores for all identifications\n- Cross-reference findings for consistency\n- Document any limitations or uncertainties' :
  currentModelConfig?.speed === 'fast' ?
  '- Balance speed with accuracy\n- Focus on primary targets\n- Provide essential details with confidence scores' :
  '- Prioritize speed and efficiency\n- Focus on obvious, high-confidence detections\n- Streamlined output with key findings'
}

Respond with structured JSON format suitable for forensic evidence documentation.`;

    // Use the current model configuration
    const isGeminiModel = currentModelConfig?.provider?.includes('Vertex AI') || selectedModel.includes('gemini');
    
    if (isGeminiModel) {
      return {
        "contents": [{
          "parts": [{
            "text": basePrompt
          }, {
            "inline_data": {
              "mime_type": "image/jpeg",
              "data": "[BASE64_ENCODED_IMAGE_DATA]"
            }
          }]
        }],
        "generationConfig": {
          "temperature": advancedParams.temperature,
          "topK": advancedParams.top_k,
          "topP": advancedParams.top_p,
          "maxOutputTokens": advancedParams.max_output_tokens,
          "candidateCount": 1,
          "frequencyPenalty": advancedParams.frequency_penalty,
          "presencePenalty": advancedParams.presence_penalty
        },
        "safetySettings": [
          {
            "category": "HARM_CATEGORY_HARASSMENT",
            "threshold": "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            "category": "HARM_CATEGORY_HATE_SPEECH", 
            "threshold": "BLOCK_MEDIUM_AND_ABOVE"
          }
        ]
      };
    } else {
      // OpenAI format (if other models are added)
      return {
        "model": selectedModel,
        "messages": [{
          "role": "user",
          "content": [{
            "type": "text",
            "text": basePrompt
          }, {
            "type": "image_url",
            "image_url": {
              "url": "data:image/jpeg;base64,[BASE64_ENCODED_IMAGE_DATA]"
            }
          }]
        }],
        "max_tokens": advancedParams.max_output_tokens,
        "temperature": advancedParams.temperature,
        "top_p": advancedParams.top_p,
        "frequency_penalty": advancedParams.frequency_penalty,
        "presence_penalty": advancedParams.presence_penalty
      };
    }
  };

  // Navigation sections - redesigned for horizontal tabs
  const sections = [
    { id: 'executive_summary', name: '📊 Overview', icon: FaEye },
    { id: 'model_comparison', name: '🤖 Models', icon: FaRobot },
    { id: 'prompt_preview', name: '🔧 Prompt Preview', icon: FaSearch },
    { id: 'cost_analysis', name: '💰 Costs', icon: FaDollarSign },
    { id: 'prompt_analysis', name: '📝 Current Prompts', icon: FaFileAlt },
    { id: 'quality_metrics', name: '🎯 Performance', icon: FaShieldAlt }
  ];

  return (
    <div style={{ 
      minHeight: '100vh',
      background: '#1a1a1a',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#ffffff'
    }}>
      {/* Clean Header */}
      <div style={{
        background: '#2a2a2a',
        borderBottom: '1px solid #404040',
        padding: '20px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <FaRobot style={{ fontSize: '32px', color: '#4f46e5' }} />
              <div>
                <h1 style={{ margin: '0', fontSize: '24px', color: '#ffffff' }}>AI Dashboard</h1>
                <p style={{ margin: '0', color: '#cccccc', fontSize: '14px' }}>Prompt engineering and model comparison</p>
              </div>
            </div>
            <button 
              onClick={exportDocumentation}
              style={{
                padding: '8px 16px',
                background: '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <FaDownload /> Export
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal Tab Navigation */}
      <div style={{
        background: '#2a2a2a',
        borderBottom: '1px solid #404040',
        padding: '0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{
            display: 'flex',
            gap: '0',
            overflowX: 'auto'
          }}>
            {sections.map(section => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  style={{
                    padding: '16px 20px',
                    border: 'none',
                    background: 'transparent',
                    color: activeSection === section.id ? '#ff6b6b' : '#cccccc',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderBottom: activeSection === section.id ? '2px solid #ff6b6b' : '2px solid transparent',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Icon />
                  {section.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>
        
        {/* Executive Summary */}
        {activeSection === 'executive_summary' && (
          <div className="content-section">
            <h2>📊 Your AI System at a Glance</h2>
            <p className="section-description">
              Here's what your AI system is currently configured to do and how you can improve it.
            </p>
            
            {/* Quick Stats */}
            <div className="quick-stats" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div className="stat-card" style={{
                background: '#2a2a2a',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                textAlign: 'center',
                border: '1px solid #e2e8f0'
              }}>
                <FaRobot className="stat-icon" style={{ fontSize: '32px', color: '#4f46e5', marginBottom: '10px' }} />
                <div className="stat-content">
                  <h3 style={{ margin: '0 0 5px 0', fontSize: '24px', color: '#1e293b' }}>{Object.keys(documentation.sections.model_comparison.models).length}</h3>
                  <p style={{ margin: '0', color: '#64748b', fontSize: '14px' }}>AI Models Available</p>
                </div>
              </div>
              <div className="stat-card">
                <FaDollarSign className="stat-icon" />
                <div className="stat-content">
                  <h3>$0.003-0.015</h3>
                  <p>Cost Range per Analysis</p>
                </div>
              </div>
              <div className="stat-card">
                <FaCheckCircle className="stat-icon" />
                <div className="stat-content">
                  <h3>85-95%</h3>
                  <p>Accuracy Range</p>
                </div>
              </div>
            </div>

            {/* Action Items for Fine-tuning */}
            <div className="action-items">
              <h3>🎯 Fine-Tuning Recommendations:</h3>
              <div className="recommendation-card">
                <FaExclamationTriangle className="rec-icon warning" />
                <div className="rec-content">
                  <h4>Check Your Costs</h4>
                  <p>Review the "💰 Costs & Budget" tab to see which AI models give you the best value for money.</p>
                </div>
              </div>
              <div className="recommendation-card">
                <FaRobot className="rec-icon info" />
                <div className="rec-content">
                  <h4>Compare AI Models</h4>
                  <p>Visit "🤖 AI Models" to see which models work best for vehicles, people, or text analysis.</p>
                </div>
              </div>
              <div className="recommendation-card">
                <FaSearch className="rec-icon success" />
                <div className="rec-content">
                  <h4>Review What AI Sees</h4>
                  <p>Check "📝 What AI Sees" to understand exactly what instructions your AI receives.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prompt Preview - NEW SECTION */}
        {activeSection === 'prompt_preview' && (
          <div className="content-section">
            <h2>🔧 Prompt Engineering Preview</h2>
            <p style={{ color: '#64748b', marginBottom: '30px', fontSize: '16px' }}>
              Select a model and targets to see <strong>exactly what prompt gets sent to AI</strong>. 
              This is your prompt engineering workspace!
            </p>

            {/* Model & Target Selection */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '30px', 
              marginBottom: '30px' 
            }}>
              {/* Model Selection */}
              <div style={{ 
                background: '#2a2a2a', 
                padding: '20px', 
                borderRadius: '12px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                border: '1px solid #404040'
              }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#ffffff' }}>🤖 Select AI Model</h3>
                <select 
                  value={selectedModel} 
                  onChange={(e) => setSelectedModel(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #404040',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: '#1a1a1a',
                    color: '#ffffff',
                    marginBottom: '12px'
                  }}
                >
                  {availableModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {model.name} ({model.speed} - {model.accuracy} accuracy - ${model.cost_per_1k_tokens}/1k tokens)
                    </option>
                  ))}
                </select>
                
                {/* Quick Preset Buttons */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => applyPreset('forensic')}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      background: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#b91c1c'}
                    onMouseOut={(e) => e.target.style.background = '#dc2626'}
                    title="Optimized for legal proceedings - maximum accuracy and consistency"
                  >
                    🎯 Forensic
                  </button>
                  <button
                    onClick={() => applyPreset('exploratory')}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      background: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#1d4ed8'}
                    onMouseOut={(e) => e.target.style.background = '#2563eb'}
                    title="Balanced for investigation - good accuracy with some flexibility"
                  >
                    🔍 Exploratory
                  </button>
                  <button
                    onClick={() => applyPreset('creative')}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      background: '#7c3aed',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#6d28d9'}
                    onMouseOut={(e) => e.target.style.background = '#7c3aed'}
                    title="Creative analysis - may catch subtle details but less consistent"
                  >
                    💡 Creative
                  </button>
                </div>
              </div>

              {/* Target Selection */}
              <div style={{ 
                background: '#2a2a2a', 
                padding: '20px', 
                borderRadius: '12px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                border: '1px solid #404040'
              }}>
                <h3 style={{ margin: '0 0 15px 0', color: '#ffffff' }}>🎯 Analysis Targets</h3>
                {['vehicles', 'people', 'text', 'objects', 'activities'].map(target => (
                  <label key={target} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '8px',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedTargets.includes(target)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTargets([...selectedTargets, target]);
                        } else {
                          setSelectedTargets(selectedTargets.filter(t => t !== target));
                        }
                      }}
                      style={{ marginRight: '5px' }}
                    />
                    <span style={{ textTransform: 'capitalize', fontSize: '14px', color: '#ffffff' }}>{target}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Advanced AI Parameters Control - Collapsible */}
            <div style={{ 
              background: '#2a2a2a', 
              padding: '15px 25px', 
              borderRadius: '12px', 
              border: '1px solid #404040',
              marginBottom: '20px'
            }}>
              <div 
                onClick={() => setShowAdvancedControls(!showAdvancedControls)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                <span style={{
                  marginRight: '10px',
                  transform: showAdvancedControls ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  color: '#4ade80'
                }}>
                  ▶
                </span>
                ⚙️ Advanced AI Parameters & Prompt Engineering Controls
                <span style={{ 
                  marginLeft: '10px', 
                  fontSize: '12px', 
                  color: '#888',
                  fontWeight: 'normal'
                }}>
                  ({showAdvancedControls ? 'Click to collapse' : 'Click to expand'})
                </span>
              </div>
              
              {showAdvancedControls && (
                <div style={{ marginTop: '15px' }}>
                  <AdvancedAIControls 
                    advancedParams={advancedParams}
                    setAdvancedParams={setAdvancedParams}
                    currentModelConfig={currentModelConfig}
                  />
                </div>
              )}
            </div>

            {/* View Mode Toggle */}
            <div style={{ 
              background: '#2a2a2a', 
              padding: '15px 25px', 
              borderRadius: '12px', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              border: '1px solid #404040',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: '0', color: '#ffffff' }}>
                  📝 Prompt Preview for {selectedModel.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h3>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {[
                    { id: 'side-by-side', label: '👥 Side by Side', desc: 'Human + API view' },
                    { id: 'human', label: '👤 Human', desc: 'Readable prompt' },
                    { id: 'api', label: '🔧 API', desc: 'Raw JSON payload' }
                  ].map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => setViewMode(mode.id)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        background: viewMode === mode.id ? '#ff6b6b' : '#1a1a1a',
                        color: viewMode === mode.id ? 'white' : '#cccccc',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                      title={mode.desc}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Generated Prompt Preview */}
            <div style={{ 
              background: '#2a2a2a', 
              padding: '25px', 
              borderRadius: '12px', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              border: '1px solid #404040'
            }}>
              {/* Conditional Content Based on View Mode */}
              {viewMode === 'side-by-side' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Human Readable Side */}
                  <div>
                    <h4 style={{ margin: '0 0 10px 0', color: '#ffffff', fontSize: '14px' }}>
                      👤 Human Readable Prompt
                    </h4>
                    <div style={{
                      background: '#1a1a1a',
                      padding: '15px',
                      borderRadius: '6px',
                      border: '1px solid #404040',
                      fontFamily: 'Monaco, Consolas, monospace',
                      fontSize: '11px',
                      lineHeight: '1.5',
                      color: '#cccccc',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '400px',
                      overflow: 'auto'
                    }}>
                      {`You are an expert forensic analyst examining video evidence. Your task is to analyze this video frame and identify the following targets with high precision:

ANALYSIS TARGETS:
${selectedTargets.map(target => `• ${target.toUpperCase()}: Detect and describe all ${target} visible in the frame`).join('\n')}

INSTRUCTIONS:
1. Examine the entire frame systematically
2. For each target type, provide:
   - Location (coordinates or descriptive position)
   - Detailed description
   - Confidence level (High/Medium/Low)
   - Any identifying features

${selectedTargets.includes('vehicles') ? `
VEHICLE ANALYSIS:
- License plates (full text if visible)
- Make, model, color, year if identifiable
- Damage, modifications, or distinctive features
- Direction of travel
` : ''}

${selectedTargets.includes('people') ? `
PERSON ANALYSIS:
- Physical description (clothing, height, build)
- Actions or behavior
- Facial features if clearly visible
- Any carried items or accessories
` : ''}

${selectedTargets.includes('text') ? `
TEXT ANALYSIS:
- All visible text (signs, documents, displays)
- Language and content
- Legibility assessment
` : ''}

OUTPUT FORMAT:
Provide results in structured JSON format with clear categories for each target type.

MODEL OPTIMIZATION FOR ${selectedModel.toUpperCase()}:
${selectedModel.includes('pro') ? 
  '- Use maximum detail and precision\n- Perform multi-pass analysis\n- Include confidence scoring' :
  selectedModel.includes('flash') && !selectedModel.includes('lite') ?
  '- Balance speed with accuracy\n- Focus on primary targets\n- Provide essential details' :
  '- Prioritize speed and efficiency\n- Focus on obvious, high-confidence detections\n- Streamlined output format'
}`}
                    </div>
                  </div>

                  {/* API Payload Side */}
                  <div>
                    <h4 style={{ margin: '0 0 10px 0', color: '#ffffff', fontSize: '14px' }}>
                      🔧 Actual API Payload ({selectedModel.includes('gemini') ? 'Gemini' : 'OpenAI'} Format)
                    </h4>
                    <div style={{
                      background: '#1e293b',
                      padding: '15px',
                      borderRadius: '6px',
                      border: '1px solid #374151',
                      fontFamily: 'Monaco, Consolas, monospace',
                      fontSize: '11px',
                      lineHeight: '1.5',
                      color: '#e2e8f0',
                      whiteSpace: 'pre-wrap',
                      maxHeight: '400px',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(generateApiPayload(), null, 2)}
                    </div>
                  </div>
                </div>
              ) : viewMode === 'api' ? (
                <div>
                  <h4 style={{ margin: '0 0 15px 0', color: '#1e293b' }}>
                    🔧 Raw API Payload ({selectedModel.includes('gemini') ? 'Gemini' : 'OpenAI'} Format)
                  </h4>
                  <div style={{
                    background: '#1e293b',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #374151',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '12px',
                    lineHeight: '1.6',
                    color: '#e2e8f0',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(generateApiPayload(), null, 2)}
                  </div>
                </div>
              ) : (
                <div>
                  <h4 style={{ margin: '0 0 15px 0', color: '#1e293b' }}>
                    👤 Human Readable Prompt
                  </h4>
                  <div style={{
                    background: '#f8fafc',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    color: '#374151',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    {`You are an expert forensic analyst examining video evidence. Your task is to analyze this video frame and identify the following targets with high precision:

ANALYSIS TARGETS:
${selectedTargets.map(target => `• ${target.toUpperCase()}: Detect and describe all ${target} visible in the frame`).join('\n')}

INSTRUCTIONS:
1. Examine the entire frame systematically
2. For each target type, provide:
   - Location (coordinates or descriptive position)
   - Detailed description
   - Confidence level (High/Medium/Low)
   - Any identifying features

${selectedTargets.includes('vehicles') ? `
VEHICLE ANALYSIS:
- License plates (full text if visible)
- Make, model, color, year if identifiable
- Damage, modifications, or distinctive features
- Direction of travel
` : ''}

${selectedTargets.includes('people') ? `
PERSON ANALYSIS:
- Physical description (clothing, height, build)
- Actions or behavior
- Facial features if clearly visible
- Any carried items or accessories
` : ''}

${selectedTargets.includes('text') ? `
TEXT ANALYSIS:
- All visible text (signs, documents, displays)
- Language and content
- Legibility assessment
` : ''}

OUTPUT FORMAT:
Provide results in structured JSON format with clear categories for each target type.

MODEL OPTIMIZATION FOR ${selectedModel.toUpperCase()}:
${selectedModel.includes('pro') ? 
  '- Use maximum detail and precision\n- Perform multi-pass analysis\n- Include confidence scoring' :
  selectedModel.includes('flash') && !selectedModel.includes('lite') ?
  '- Balance speed with accuracy\n- Focus on primary targets\n- Provide essential details' :
  '- Prioritize speed and efficiency\n- Focus on obvious, high-confidence detections\n- Streamlined output format'
}`}
                  </div>
                </div>
              )}
              
              {/* Prompt Stats */}
              <div style={{ 
                marginTop: '15px', 
                padding: '15px', 
                background: '#1a1a1a', 
                borderRadius: '8px',
                border: '1px solid #404040',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '13px',
                color: '#cccccc'
              }}>
                <span>📊 Estimated tokens: ~{Math.floor(400 + (selectedTargets.length * 50) + 
                  (advancedParams.verbosity === 'comprehensive' ? 200 : advancedParams.verbosity === 'detailed' ? 100 : 0) + 
                  (advancedParams.chain_of_thought && advancedParams.chain_of_thought_enabled !== false ? 150 : 0) +
                  (advancedParams.few_shot_learning && advancedParams.few_shot_learning_enabled !== false ? 200 : 0) +
                  (advancedParams.multi_agent_reasoning && advancedParams.multi_agent_reasoning_enabled !== false ? 100 : 0) +
                  (advancedParams.retrieval_augmented && advancedParams.retrieval_augmented_enabled !== false ? 75 : 0)
                )}</span>
                <span>💰 Est. cost per analysis: ${currentModelConfig ? `$${currentModelConfig.cost_per_1k_tokens.toFixed(4)}` : '$0.0016'}</span>
                <span>⚡ Speed: {currentModelConfig ? currentModelConfig.speed : 'Fast'}</span>
              </div>
            </div>

            {/* Forensic Prompt Structure Breakdown - Always Visible */}
            <div style={{ 
              background: '#2a2a2a', 
              padding: '25px', 
              borderRadius: '12px', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              border: '1px solid #404040',
              marginTop: '20px'
            }}>
              <h4 style={{ margin: '0 0 20px 0', color: '#ffffff', fontSize: '16px' }}>
                📋 Forensic Prompt Structure Breakdown
              </h4>
              
              {/* Prompt Components */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                
                {/* System Role */}
                <div style={{ 
                  background: '#1a1a1a', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  border: '1px solid #404040' 
                }}>
                  <h5 style={{ color: '#ff6b6b', margin: '0 0 10px 0', fontSize: '14px' }}>
                    🎯 System Role & Requirements
                  </h5>
                  <div style={{ color: '#cccccc', fontSize: '12px', lineHeight: '1.4' }}>
                    • Professional forensic AI analyst for criminal investigations<br/>
                    • Legal proceedings standards (factual observations only)<br/>
                    • Confidence scores (0.0-1.0 scale) for all detections<br/>
                    • Forensic terminology and chain of custody awareness
                  </div>
                </div>

                {/* Selected Targets */}
                {selectedTargets.includes('vehicles') && (
                  <div style={{ 
                    background: '#1a1a1a', 
                    padding: '15px', 
                    borderRadius: '8px', 
                    border: '1px solid #404040' 
                  }}>
                    <h5 style={{ color: '#4ade80', margin: '0 0 10px 0', fontSize: '14px' }}>
                      🚗 Vehicle Analysis Protocol
                    </h5>
                    <div style={{ color: '#cccccc', fontSize: '12px', lineHeight: '1.4' }}>
                      <strong>1. Identification:</strong> Make, model, year, body type, colors<br/>
                      <strong>2. License Plates:</strong> Character-by-character confidence scoring<br/>
                      <strong>3. Features:</strong> Damage, modifications, stickers, markings<br/>
                      <strong>4. Condition:</strong> Paint, tires, maintenance indicators<br/>
                      <strong>5. Movement:</strong> Direction, speed, driving behavior patterns
                    </div>
                  </div>
                )}

                {selectedTargets.includes('activities') && (
                  <div style={{ 
                    background: '#1a1a1a', 
                    padding: '15px', 
                    borderRadius: '8px', 
                    border: '1px solid #404040' 
                  }}>
                    <h5 style={{ color: '#60a5fa', margin: '0 0 10px 0', fontSize: '14px' }}>
                      🏃 Activity Analysis Protocol
                    </h5>
                    <div style={{ color: '#cccccc', fontSize: '12px', lineHeight: '1.4' }}>
                      <strong>1. Primary Actions:</strong> Walking, running, object manipulation<br/>
                      <strong>2. Interactions:</strong> Group dynamics, communication indicators<br/>
                      <strong>3. Behavioral:</strong> Concealment, surveillance awareness<br/>
                      <strong>4. Temporal:</strong> Sequence, timing, urgency patterns<br/>
                      <strong>5. Environmental:</strong> Evidence tampering, hiding actions
                    </div>
                  </div>
                )}

                {selectedTargets.includes('people') && (
                  <div style={{ 
                    background: '#1a1a1a', 
                    padding: '15px', 
                    borderRadius: '8px', 
                    border: '1px solid #404040' 
                  }}>
                    <h5 style={{ color: '#f59e0b', margin: '0 0 10px 0', fontSize: '14px' }}>
                      👤 People Analysis (Privacy-Compliant)
                    </h5>
                    <div style={{ color: '#cccccc', fontSize: '12px', lineHeight: '1.4' }}>
                      <strong>Focus:</strong> Clothing, behavior, interactions (not biometric ID)<br/>
                      <strong>Privacy:</strong> No facial recognition or personal identification<br/>
                      <strong>Descriptors:</strong> General physical characteristics, carried items
                    </div>
                  </div>
                )}

                {selectedTargets.includes('text') && (
                  <div style={{ 
                    background: '#1a1a1a', 
                    padding: '15px', 
                    borderRadius: '8px', 
                    border: '1px solid #404040' 
                  }}>
                    <h5 style={{ color: '#a78bfa', margin: '0 0 10px 0', fontSize: '14px' }}>
                      📝 Text Analysis Protocol
                    </h5>
                    <div style={{ color: '#cccccc', fontSize: '12px', lineHeight: '1.4' }}>
                      <strong>Detection:</strong> Signs, documents, displays, license plates<br/>
                      <strong>Assessment:</strong> Legibility, language, content relevance<br/>
                      <strong>Extraction:</strong> Complete text with confidence scores
                    </div>
                  </div>
                )}

                {/* Confidence Scoring */}
                <div style={{ 
                  background: '#1a1a1a', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  border: '1px solid #404040' 
                }}>
                  <h5 style={{ color: '#ec4899', margin: '0 0 10px 0', fontSize: '14px' }}>
                    📊 Confidence Scoring System
                  </h5>
                  <div style={{ color: '#cccccc', fontSize: '12px', lineHeight: '1.4' }}>
                    <strong>0.9-1.0:</strong> Clearly visible, unambiguous identification<br/>
                    <strong>0.7-0.8:</strong> Clearly visible with minor uncertainty<br/>
                    <strong>0.5-0.6:</strong> Partially visible or obscured<br/>
                    <strong>0.3-0.4:</strong> Barely visible, high uncertainty<br/>
                    <strong>0.0-0.2:</strong> Cannot determine reliably
                  </div>
                </div>

                {/* Model Optimization */}
                <div style={{ 
                  background: '#1a1a1a', 
                  padding: '15px', 
                  borderRadius: '8px', 
                  border: '1px solid #404040' 
                }}>
                  <h5 style={{ color: '#06b6d4', margin: '0 0 10px 0', fontSize: '14px' }}>
                    ⚙️ Model-Specific Optimization ({currentModelConfig?.name})
                  </h5>
                  <div style={{ color: '#cccccc', fontSize: '12px', lineHeight: '1.4' }}>
                    <strong>Accuracy:</strong> {currentModelConfig?.accuracy || 'Standard'}<br/>
                    <strong>Speed:</strong> {currentModelConfig?.speed || 'Standard'}<br/>
                    <strong>Cost:</strong> ${currentModelConfig?.cost_per_1k_tokens?.toFixed(4) || '0.0016'} per 1k tokens<br/>
                    <strong>Optimization:</strong> {
                      currentModelConfig?.accuracy === 'highest' ? 
                      'Comprehensive analysis with detailed reasoning and cross-referencing' :
                      currentModelConfig?.speed === 'fastest' ?
                      'Speed-optimized with focus on high-confidence detections' :
                      'Balanced approach with essential details and confidence scoring'
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Model Comparison */}
        {activeSection === 'model_comparison' && (
          <div className="content-section">
            <h2>🤖 AI Models - Choose the Best for Your Needs</h2>
            <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '16px' }}>
              Compare different AI models to find the best balance of speed, accuracy, and cost for your investigations. 
              <strong> Tip:</strong> Use cheaper models for quick scans, premium models for detailed analysis.
            </p>
            
            {/* Search Bar */}
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search models by name, provider, or capabilities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Models Grid */}
            <div className="models-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '20px',
              marginTop: '20px'
            }}>
              {filteredModels.map(model => (
                <div key={model.id} className="model-card" style={{
                  background: '#2a2a2a',
                  padding: '24px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid #e2e8f0'
                }}>
                  <div className="model-header">
                    <h3>{model.name}</h3>
                    <span className="model-provider">{model.provider}</span>
                  </div>
                  <div className="model-stats">
                    <div className="stat">
                      <FaClock className="stat-icon" />
                      <span className="stat-label">Speed:</span>
                      <span className="stat-value">{model.speed}</span>
                    </div>
                    <div className="stat">
                      <FaShieldAlt className="stat-icon" />
                      <span className="stat-label">Accuracy:</span>
                      <span className="stat-value">{model.accuracy}</span>
                    </div>
                    <div className="stat">
                      <FaDollarSign className="stat-icon" />
                      <span className="stat-label">Cost/1K tokens:</span>
                      <span className="stat-value">${model.cost_per_1k_tokens}</span>
                    </div>
                  </div>
                  <div className="model-details">
                    <div className="detail">
                      <strong>Best for:</strong> {model.best_for}
                    </div>
                    <div className="detail">
                      <strong>Capabilities:</strong> {model.capabilities}
                    </div>
                    <div className="detail">
                      <strong>Max tokens:</strong> {model.max_tokens.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Prompt Analysis */}
        {activeSection === 'prompt_analysis' && (
          <div className="content-section">
            <h2>{documentation.sections.prompt_analysis.title}</h2>
            
            {/* Base Prompts */}
            <div className="prompt-section">
              <h3>Base Forensic Prompt Templates</h3>
              {documentation.sections.prompt_analysis.base_prompts.map(prompt => (
                <div key={prompt.template_name} className="prompt-card">
                  <h4>{prompt.template_name.replace(/_/g, ' ').toUpperCase()}</h4>
                  <div className="prompt-stats">
                    <span>Words: {prompt.word_count}</span>
                    <span>Characters: {prompt.character_count}</span>
                  </div>
                  <div className="prompt-analysis">
                    <div className="analysis-item">
                      <strong>Key Forensic Phrases:</strong>
                      <div className="tag-list">
                        {prompt.forensic_language.map((phrase, i) => (
                          <span key={i} className="tag forensic-tag">{phrase}</span>
                        ))}
                      </div>
                    </div>
                    <div className="analysis-item">
                      <strong>Compliance Keywords:</strong>
                      <div className="tag-list">
                        {prompt.compliance_keywords.map((keyword, i) => (
                          <span key={i} className="tag compliance-tag">{keyword}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Model-Specific Prompts */}
            <div className="prompt-section">
              <h3>Model-Specific Prompt Analysis</h3>
              {documentation.sections.prompt_analysis.model_specific_prompts.map(prompt => (
                <div key={prompt.model_id} className="prompt-card">
                  <h4>{prompt.model_name}</h4>
                  <div className="prompt-metrics">
                    <div className="metric">
                      <span className="metric-label">Prompt Length:</span>
                      <span className="metric-value">{prompt.prompt_length} chars</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Est. Tokens:</span>
                      <span className="metric-value">{prompt.estimated_tokens}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">JSON Schema Size:</span>
                      <span className="metric-value">{prompt.json_schema_size} chars</span>
                    </div>
                  </div>
                  <div className="key-instructions">
                    <strong>Key Instructions:</strong>
                    <ul>
                      {prompt.key_instructions.slice(0, 3).map((instruction, i) => (
                        <li key={i}>{instruction}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quality Metrics */}
        {activeSection === 'quality_metrics' && (
          <div className="content-section">
            <h2>{documentation.sections.quality_metrics.title}</h2>
            
            <div className="quality-grid">
              {/* Prompt Objectivity */}
              <div className="quality-card">
                <h3>Prompt Objectivity</h3>
                <div className="score-display">
                  <span className="score">{documentation.sections.quality_metrics.forensic_standards.prompt_objectivity.score}/10</span>
                  <FaCheckCircle className="score-icon good" />
                </div>
                <p>{documentation.sections.quality_metrics.forensic_standards.prompt_objectivity.rationale}</p>
                <div className="improvements">
                  <strong>Improvements:</strong>
                  <ul>
                    {documentation.sections.quality_metrics.forensic_standards.prompt_objectivity.improvements.map((improvement, i) => (
                      <li key={i}>{improvement}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Legal Compliance */}
              <div className="quality-card">
                <h3>Legal Compliance</h3>
                <div className="score-display">
                  <span className="score">{documentation.sections.quality_metrics.forensic_standards.legal_compliance.score}/10</span>
                  <FaShieldAlt className="score-icon good" />
                </div>
                <div className="standards-met">
                  <strong>Standards Met:</strong>
                  <ul>
                    {documentation.sections.quality_metrics.forensic_standards.legal_compliance.standards_met.map((standard, i) => (
                      <li key={i}>
                        <FaCheckCircle className="check-icon" />
                        {standard}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Bias Assessment */}
              <div className="quality-card">
                <h3>Bias Risk Assessment</h3>
                <div className="risk-display">
                  <span className={`risk-level ${documentation.sections.quality_metrics.forensic_standards.bias_assessment.risk_level.toLowerCase()}`}>
                    {documentation.sections.quality_metrics.forensic_standards.bias_assessment.risk_level}
                  </span>
                </div>
                <div className="mitigation">
                  <strong>Mitigation Measures:</strong>
                  <ul>
                    {documentation.sections.quality_metrics.forensic_standards.bias_assessment.mitigation.map((measure, i) => (
                      <li key={i}>{measure}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cost Analysis */}
        {activeSection === 'cost_analysis' && (
          <div className="content-section">
            <h2>{documentation.sections.cost_analysis.title}</h2>
            
            <div className="cost-table">
              <table>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Cost per 1K tokens</th>
                    <th>Est. tokens per analysis</th>
                    <th>Cost per analysis</th>
                    <th>Cost effectiveness</th>
                  </tr>
                </thead>
                <tbody>
                  {documentation.sections.cost_analysis.cost_breakdown.map((item, i) => (
                    <tr key={i}>
                      <td>{item.model}</td>
                      <td>${item.cost_per_1k_tokens}</td>
                      <td>{item.estimated_tokens_per_analysis}</td>
                      <td>${item.cost_per_analysis.toFixed(2)}</td>
                      <td>
                        <span className={`effectiveness ${item.cost_effectiveness_rating.toLowerCase()}`}>
                          {item.cost_effectiveness_rating}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PromptDashboard;